// Corpus-level merge-queue review screen (PRD 02 §Corpus reviewer surface).
//
// Lists every MergeProposal from Pass 2. Each proposal shows:
//   - the cluster's member refs ("doc-a:t-1, doc-b:t-1")
//   - the synthesized canonical Definition (plain + precise)
//   - cluster cosine confidence
//   - three-way decision toggle (pending | accepted | rejected)
//
// A "Run reconcile" button up top triggers POST /corpora/:id/reconcile
// for cases where the queue is empty or stale.

import { useMemo } from "react";
import type { Definition } from "../../data/types";
import {
  type MergeProposal,
  type MergeReviewState,
  useCorpusMerges,
  useCorpusRejections,
  useDecideMerge,
  useReconcileCorpus,
} from "./merges-api";

import "./merge-queue.css";

interface Props {
  corpusId: string;
  actor?: string;
}

export function MergeQueuePage({ corpusId, actor = "anonymous" }: Props) {
  const { data, isLoading, error } = useCorpusMerges(corpusId);
  const rejections = useCorpusRejections(corpusId);
  const reconcile = useReconcileCorpus(corpusId);
  const decide = useDecideMerge(corpusId);

  const grouped = useMemo(() => {
    const out: Record<MergeReviewState, MergeProposal[]> = {
      pending: [], accepted: [], rejected: [], edited: [],
    };
    for (const p of data?.proposals ?? []) {
      out[p.review_state].push(p);
    }
    return out;
  }, [data]);

  if (isLoading) {
    return (
      <div className="maisight-merge-queue-loading" role="status">
        Loading merge queue…
      </div>
    );
  }
  if (error) {
    return (
      <div className="maisight-merge-queue-error" role="alert">
        Failed to load: {error.message}
      </div>
    );
  }

  const empty = !data || data.proposals.length === 0;

  return (
    <div className="maisight-merge-queue">
      <header className="maisight-merge-queue-header">
        <div>
          <h1>Merge queue — {corpusId}</h1>
          <p>
            Pass 2 clusters proposed by embedding similarity. Accept to
            keep the canonical entity; reject to keep the cluster
            members as separate canonical entities.
          </p>
        </div>
        <div className="maisight-merge-queue-actions">
          <button
            onClick={() => reconcile.mutate()}
            disabled={reconcile.isPending}
          >
            {reconcile.isPending ? "Reconciling…" : "Run reconcile"}
          </button>
        </div>
      </header>

      {reconcile.error && (
        <p role="alert" className="maisight-merge-queue-error-msg">
          {reconcile.error.message}
        </p>
      )}
      {reconcile.data && (
        <p className="maisight-merge-queue-run-summary">
          Last run: {reconcile.data.candidates_count} candidates →{" "}
          {reconcile.data.auto_merges_proposed} auto-merges,{" "}
          {reconcile.data.fuzzy_proposed} fuzzy,{" "}
          {reconcile.data.canonical_entities} canonical entities.
        </p>
      )}

      {empty ? (
        <div className="maisight-merge-queue-empty">
          <p>No merge proposals yet.</p>
          <p>
            Click <strong>Run reconcile</strong> once at least one doc in
            this corpus has a published glossary.
          </p>
        </div>
      ) : (
        <>
          <Section
            title={`Pending (${grouped.pending.length})`}
            proposals={grouped.pending}
            corpusId={corpusId}
            decide={(proposalId, action) =>
              decide.mutate({ proposalId, action, actor })
            }
          />
          <Section
            title={`Accepted (${grouped.accepted.length})`}
            proposals={grouped.accepted}
            corpusId={corpusId}
            collapsible
            decide={(proposalId, action) =>
              decide.mutate({ proposalId, action, actor })
            }
          />
          <Section
            title={`Rejected (${grouped.rejected.length})`}
            proposals={grouped.rejected}
            corpusId={corpusId}
            collapsible
            decide={(proposalId, action) =>
              decide.mutate({ proposalId, action, actor })
            }
          />
        </>
      )}

      <RejectionHistory
        pairs={rejections.data?.pairs ?? []}
        loading={rejections.isLoading}
      />
    </div>
  );
}

function RejectionHistory({
  pairs, loading,
}: {
  pairs: Array<[string, string]>;
  loading: boolean;
}) {
  if (loading && pairs.length === 0) return null;
  return (
    <details className="maisight-merge-section maisight-rejection-history">
      <summary>
        <h2>Rejection history ({pairs.length})</h2>
      </summary>
      {pairs.length === 0 ? (
        <p className="maisight-rejection-empty">
          No rejections yet — Pass 2 has never been asked to forget a cluster.
        </p>
      ) : (
        <ul className="maisight-rejection-list">
          {pairs.map(([a, b], i) => (
            <li key={`${a}-${b}-${i}`}>
              <code>{a}</code> <span aria-hidden="true">×</span> <code>{b}</code>
            </li>
          ))}
        </ul>
      )}
      <p className="maisight-rejection-help">
        These ref pairs are pruned from every future reconcile so the
        reviewer's decision survives across runs.
      </p>
    </details>
  );
}

interface SectionProps {
  title: string;
  proposals: MergeProposal[];
  corpusId: string;
  collapsible?: boolean;
  decide: (proposalId: string, action: "accept" | "reject") => void;
}

function Section({
  title, proposals, collapsible, decide,
}: SectionProps) {
  if (proposals.length === 0) return null;
  const content = (
    <ul className="maisight-merge-list">
      {proposals.map((p) => (
        <li key={p.proposal_id}>
          <ProposalCard proposal={p} decide={decide} />
        </li>
      ))}
    </ul>
  );
  if (!collapsible) {
    return (
      <section className="maisight-merge-section">
        <h2>{title}</h2>
        {content}
      </section>
    );
  }
  return (
    <details className="maisight-merge-section">
      <summary><h2>{title}</h2></summary>
      {content}
    </details>
  );
}

interface CardProps {
  proposal: MergeProposal;
  decide: (proposalId: string, action: "accept" | "reject") => void;
}

function ProposalCard({ proposal, decide }: CardProps) {
  return (
    <article
      className="maisight-merge-card"
      data-kind={proposal.kind}
      data-state={proposal.review_state}
    >
      <header>
        <span className="maisight-merge-kind">{proposal.kind}</span>
        <span className="maisight-merge-confidence">
          cos {proposal.confidence.toFixed(3)}
        </span>
        <span className="maisight-merge-state">{proposal.review_state}</span>
      </header>

      <p className="maisight-merge-members">
        {proposal.cluster_member_refs.map((ref, i) => (
          <span key={ref}>
            <code>{ref}</code>
            {i < proposal.cluster_member_refs.length - 1 ? " · " : ""}
          </span>
        ))}
      </p>

      {proposal.proposed_canonical ? (
        <CanonicalView definition={proposal.proposed_canonical} />
      ) : (
        <p className="maisight-merge-no-canonical">
          {proposal.kind === "fuzzy_match"
            ? "Reviewer must author a canonical Definition."
            : "Synthesis returned empty; reviewer arbitrates."}
        </p>
      )}

      <div className="maisight-merge-actions">
        <button
          onClick={() => decide(proposal.proposal_id, "accept")}
          disabled={proposal.review_state === "accepted"}
          className="maisight-merge-accept"
        >
          Accept
        </button>
        <button
          onClick={() => decide(proposal.proposal_id, "reject")}
          disabled={proposal.review_state === "rejected"}
          className="maisight-merge-reject"
        >
          Reject
        </button>
      </div>
    </article>
  );
}

function CanonicalView({ definition }: { definition: Definition }) {
  return (
    <div className="maisight-merge-canonical">
      <p className="maisight-merge-canonical-plain">
        {definition.definition_plain}
      </p>
      {definition.definition_precise &&
        definition.definition_precise !== definition.definition_plain && (
          <details>
            <summary>show precise</summary>
            <p>{definition.definition_precise}</p>
          </details>
        )}
      {definition.prerequisites && definition.prerequisites.length > 0 && (
        <p className="maisight-merge-canonical-prereqs">
          Builds on:{" "}
          {definition.prerequisites.map((p) => (
            <span key={p} className="maisight-merge-canonical-prereq-chip">{p}</span>
          ))}
        </p>
      )}
    </div>
  );
}
